import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

// Import translation files
import commonEN from "./locales/en/common.json";
import proceduresEN from "./locales/en/procedures.json";
import quoteEN from "./locales/en/quote.json";
import campaignActionEN from "./locales/en/campaignAction.json";
import patientPortalEN from "./locales/en/patientPortal.json";

import commonES from "./locales/es/common.json";
import proceduresES from "./locales/es/procedures.json";
import quoteES from "./locales/es/quote.json";
import campaignActionES from "./locales/es/campaignAction.json";
import patientPortalES from "./locales/es/patientPortal.json";

// Custom language detector to handle URL query params
const customQueryDetector = {
  name: "customQuery",
  lookup() {
    const searchParams = new URLSearchParams(window.location.search);
    const lang = searchParams.get("language") || searchParams.get("lang");
    if (process.env.REACT_APP_TARGET_ENV !== "production") {
      console.log("[i18n] customQuery detector - Query param detected:", lang);
    }
    return lang || undefined;
  },
  cacheUserLanguage(lng: string) {
    // Store in localStorage for persistence
    if (process.env.REACT_APP_TARGET_ENV !== "production") {
      console.log("[i18n] customQuery detector - Caching language:", lng);
    }
    localStorage.setItem("i18nextLng", lng);
  },
};

const languageDetector = new LanguageDetector();
languageDetector.addDetector(customQueryDetector);

i18n
  .use(languageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: {
        common: commonEN,
        procedures: proceduresEN,
        quote: quoteEN,
        campaignAction: campaignActionEN,
        patientPortal: patientPortalEN,
      },
      "es-US": {
        common: commonES,
        procedures: proceduresES,
        quote: quoteES,
        campaignAction: campaignActionES,
        patientPortal: patientPortalES,
      },
      es: {
        common: commonES,
        procedures: proceduresES,
        quote: quoteES,
        campaignAction: campaignActionES,
        patientPortal: patientPortalES,
      },
    },
    fallbackLng: "en",
    defaultNS: "common",
    ns: ["common", "procedures", "quote", "campaignAction", "patientPortal"],

    detection: {
      // Order matters - first detector that returns a value wins
      order: ["customQuery", "localStorage", "navigator"],
      caches: ["localStorage"],
      // Note: 'customQuery' uses our custom detector defined above
      lookupLocalStorage: "i18nextLng",
    },

    interpolation: {
      escapeValue: false, // React already escapes
    },

    react: {
      // Translations are bundled synchronously via `resources` above, so there
      // is nothing to wait for. Disabling Suspense avoids transient fallback
      // flashes during initial render.
      useSuspense: false,
    },
  });

const isProduction = process.env.REACT_APP_TARGET_ENV === "production";

// Debug: Log current language and available resources
if (!isProduction) {
  console.log("[i18n] Initialized with language:", i18n.language);
  console.log(
    "[i18n] Available languages:",
    Object.keys(i18n.options.resources || {}),
  );
}

// Track the source of language changes
let isChangingLanguage = false;

// Listen to all language changes
i18n.on("languageChanged", (lng) => {
  if (!isProduction) {
    console.log("[i18n] languageChanged event fired, new language:", lng);
    console.log(
      "[i18n] Available resources for",
      lng,
      ":",
      Object.keys(i18n.options.resources?.[lng] || {}),
    );
    console.log("[i18n] Stack trace:", new Error().stack);
  }
});

// Log when resources are loaded
i18n.on("loaded", (loaded) => {
  if (!isProduction) {
    console.log("[i18n] Resources loaded:", loaded);
  }
});

// Override changeLanguage to add logging
const originalChangeLanguage = i18n.changeLanguage.bind(i18n);
(i18n as any).changeLanguage = (lng: string, ...args: any[]) => {
  if (!isProduction) {
    console.log(
      "[i18n] changeLanguage CALLED with:",
      lng,
      "current language:",
      i18n.language,
    );
    console.log("[i18n] Call stack:", new Error().stack);
  }
  isChangingLanguage = true;
  const result = originalChangeLanguage(lng, ...args);
  result.finally(() => {
    isChangingLanguage = false;
    if (!isProduction) {
      console.log(
        "[i18n] changeLanguage COMPLETED, final language:",
        i18n.language,
      );
    }
  });
  return result;
};

export default i18n;
